/**
 * Leviathan Web - Shipments Page
 * Quasar Prime LLC
 * Delivery Tracking & BOL Management
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  RefreshCw,
  Search,
  Filter,
  Package,
  Truck,
  User,
  Calendar,
  FileText,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  TrendingUp,
  Send,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';

interface ShipmentItem {
  id: string;
  productId: string;
  orderedQuantity: number;
  shippedQuantity: number;
  deliveredQuantity: number | null;
  unit: string;
  unitPrice: number;
  lineTotal: number;
  product: {
    id: string;
    name: string;
    sku: string;
    unit: string;
  };
}

interface Shipment {
  id: string;
  shipmentNumber: string;
  bolNumber: string | null;
  status: string;
  shipDate: string;
  deliveryDate: string | null;
  deliveredQuantity: number | null;
  deliveryAddress: string | null;
  deliveryCity: string | null;
  deliveryState: string | null;
  receivedBy: string | null;
  order: {
    id: string;
    orderNumber: string;
    status: string;
    total: number;
  };
  dispatch: {
    id: string;
    dispatchNumber: string;
    status: string;
  } | null;
  customer: {
    id: string;
    name: string;
    customerNumber: string;
  };
  truck: {
    id: string;
    unitNumber: string;
    name: string | null;
  } | null;
  driver: {
    id: string;
    driverNumber: string;
    firstName: string;
    lastName: string;
  } | null;
  items: ShipmentItem[];
}

interface ShipmentStats {
  todayShipments: number;
  pendingShipments: number;
  inTransitShipments: number;
  deliveredToday: number;
  deliveredThisWeek: number;
  deliveredThisMonth: number;
  byStatus: {
    pending: number;
    inTransit: number;
    delivered: number;
    partial: number;
    failed: number;
    cancelled: number;
  };
}

const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; bgColor: string; icon: React.ElementType }
> = {
  PENDING: {
    label: 'Pending',
    color: 'text-steel',
    bgColor: 'bg-steel/20 border-steel/50',
    icon: Clock,
  },
  IN_TRANSIT: {
    label: 'In Transit',
    color: 'text-amber',
    bgColor: 'bg-amber/20 border-amber/50',
    icon: Truck,
  },
  DELIVERED: {
    label: 'Delivered',
    color: 'text-success-green',
    bgColor: 'bg-success-green/20 border-success-green/50',
    icon: CheckCircle,
  },
  PARTIAL: {
    label: 'Partial',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-400/20 border-yellow-400/50',
    icon: TrendingUp,
  },
  FAILED: {
    label: 'Failed',
    color: 'text-error-red',
    bgColor: 'bg-error-red/20 border-error-red/50',
    icon: AlertTriangle,
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'text-text-tertiary',
    bgColor: 'bg-panel-border/20 border-panel-border/50',
    icon: XCircle,
  },
};

const ShipmentsPage = () => {
  const { accessToken } = useAuth();
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [stats, setStats] = useState<ShipmentStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const fetchShipments = async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (statusFilter) {
        params.append('status', statusFilter);
      }

      const response = await fetch(`${API_URL}/api/shipments?${params}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setShipments(data.data);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Error fetching shipments:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/shipments/stats`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching shipment stats:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchShipments(), fetchStats()]);
      setIsLoading(false);
    };
    loadData();
  }, [pagination.page, searchTerm, statusFilter, accessToken]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination({ ...pagination, page: 1 });
    fetchShipments();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6 relative">
      {/* Header with geometric frame */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Shipments
              </h1>
              <p className="text-text-secondary text-sm mt-1 font-mono">
                Delivery Tracking & Bill of Lading Management
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  fetchShipments();
                  fetchStats();
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-panel-dark hover:bg-steel/20 text-text-secondary hover:text-steel border border-panel-border hover:border-steel transition-all"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="text-xs font-mono uppercase">Refresh</span>
              </button>
              <Link
                to="/shipments/new"
                className="flex items-center space-x-2 px-6 py-2 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all"
                style={{ boxShadow: '0 0 15px rgba(255, 149, 0, 0.4)' }}
              >
                <Plus className="w-5 h-5" />
                <span>New Shipment</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <StatCard
          title="Today's Shipments"
          value={stats?.todayShipments ?? 0}
          color="steel"
          icon={Calendar}
        />
        <StatCard
          title="Pending"
          value={stats?.pendingShipments ?? 0}
          color="steel"
          icon={Clock}
        />
        <StatCard
          title="In Transit"
          value={stats?.inTransitShipments ?? 0}
          color="amber"
          icon={Truck}
        />
        <StatCard
          title="Delivered Today"
          value={stats?.deliveredToday ?? 0}
          color="success-green"
          icon={CheckCircle}
        />
        <StatCard
          title="This Week"
          value={stats?.deliveredThisWeek ?? 0}
          color="success-green"
          icon={TrendingUp}
        />
        <StatCard
          title="This Month"
          value={stats?.deliveredThisMonth ?? 0}
          color="success-green"
          icon={Package}
        />
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-panel-dark border border-panel-border p-4">
        <form onSubmit={handleSearch} className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-tertiary" />
            <input
              type="text"
              placeholder="Search by shipment #, BOL #, order #, or customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-background border border-panel-border text-text-primary placeholder-text-tertiary focus:outline-none focus:border-amber transition-colors font-mono text-sm"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-tertiary" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination({ ...pagination, page: 1 });
              }}
              className="pl-9 pr-8 py-2 bg-background border border-panel-border text-text-primary focus:outline-none focus:border-amber transition-colors font-mono text-sm appearance-none cursor-pointer"
            >
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="IN_TRANSIT">In Transit</option>
              <option value="DELIVERED">Delivered</option>
              <option value="PARTIAL">Partial</option>
              <option value="FAILED">Failed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          <button
            type="submit"
            className="px-6 py-2 bg-steel hover:bg-steel/80 text-background font-mono uppercase text-sm transition-all"
          >
            Search
          </button>
        </form>
      </div>

      {/* Shipments Table */}
      <div className="bg-panel-dark border border-panel-border">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex items-center space-x-4">
              <div className="w-3 h-3 bg-amber animate-pulse" />
              <span className="text-text-secondary font-mono">Loading shipments...</span>
            </div>
          </div>
        ) : shipments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Package className="w-16 h-16 text-text-tertiary mb-4" />
            <p className="text-text-secondary font-mono">No shipments found</p>
            <Link
              to="/shipments/new"
              className="mt-4 flex items-center space-x-2 px-4 py-2 text-amber border border-amber hover:bg-amber/20 transition-all font-mono text-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Create First Shipment</span>
            </Link>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-4 p-4 border-b border-panel-border bg-background/50 text-xs font-mono uppercase text-text-tertiary tracking-wider">
              <div className="col-span-2">Shipment / BOL</div>
              <div className="col-span-2">Order / Customer</div>
              <div className="col-span-2">Ship Date</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2">Products</div>
              <div className="col-span-2">Truck / Driver</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>

            {/* Table Body */}
            {shipments.map((shipment) => (
              <ShipmentRow key={shipment.id} shipment={shipment} formatDate={formatDate} />
            ))}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-panel-border">
                <p className="text-text-tertiary text-sm font-mono">
                  Showing {(pagination.page - 1) * pagination.limit + 1} -{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total}
                </p>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                    disabled={pagination.page === 1}
                    className="px-3 py-1 bg-background border border-panel-border text-text-secondary hover:border-steel hover:text-steel disabled:opacity-50 disabled:cursor-not-allowed transition-all font-mono text-sm"
                  >
                    Previous
                  </button>
                  {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPagination({ ...pagination, page: pageNum })}
                        className={`px-3 py-1 border font-mono text-sm transition-all ${
                          pagination.page === pageNum
                            ? 'bg-amber text-background border-amber'
                            : 'bg-background border-panel-border text-text-secondary hover:border-amber hover:text-amber'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                    disabled={pagination.page === pagination.pages}
                    className="px-3 py-1 bg-background border border-panel-border text-text-secondary hover:border-steel hover:text-steel disabled:opacity-50 disabled:cursor-not-allowed transition-all font-mono text-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

function StatCard({
  title,
  value,
  color,
  icon: Icon,
}: {
  title: string;
  value: number;
  color: 'steel' | 'amber' | 'success-green' | 'error-red';
  icon: React.ElementType;
}) {
  const colorClasses = {
    steel: 'text-steel border-steel/30 shadow-[0_0_10px_rgba(92,140,199,0.2)]',
    amber: 'text-amber border-amber/30 shadow-[0_0_10px_rgba(255,149,0,0.2)]',
    'success-green':
      'text-success-green border-success-green/30 shadow-[0_0_10px_rgba(34,197,94,0.2)]',
    'error-red': 'text-error-red border-error-red/30 shadow-[0_0_10px_rgba(239,68,68,0.2)]',
  };

  const classes = colorClasses[color];

  return (
    <div
      className={`relative stat-card bg-panel-dark border ${classes} p-3 clip-path-octagon`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-text-tertiary text-xs uppercase tracking-widest font-mono mb-1">
            {title}
          </p>
          <p className={`text-2xl font-display font-bold ${classes.split(' ')[0]}`}>
            {value}
          </p>
        </div>
        <Icon className={`w-8 h-8 ${classes.split(' ')[0]} opacity-50`} />
      </div>
    </div>
  );
}

function ShipmentRow({
  shipment,
  formatDate,
}: {
  shipment: Shipment;
  formatDate: (date: string) => string;
}) {
  const config = (STATUS_CONFIG[shipment.status] || STATUS_CONFIG.PENDING)!;
  const StatusIcon = config.icon;
  const totalShipped = shipment.items.reduce((sum, item) => sum + item.shippedQuantity, 0);

  return (
    <Link
      to={`/shipments/${shipment.id}`}
      className="grid grid-cols-12 gap-4 p-4 border-b border-panel-border hover:bg-background/50 transition-colors group"
    >
      {/* Shipment / BOL */}
      <div className="col-span-2">
        <p className="text-amber font-mono text-sm group-hover:text-amber">
          {shipment.shipmentNumber}
        </p>
        {shipment.bolNumber && (
          <div className="flex items-center space-x-1 mt-1">
            <FileText className="w-3 h-3 text-steel" />
            <p className="text-text-tertiary text-xs font-mono">{shipment.bolNumber}</p>
          </div>
        )}
      </div>

      {/* Order / Customer */}
      <div className="col-span-2">
        <p className="text-text-primary text-sm truncate">{shipment.customer.name}</p>
        <p className="text-text-tertiary text-xs font-mono">{shipment.order.orderNumber}</p>
      </div>

      {/* Ship Date */}
      <div className="col-span-2">
        <div className="flex items-center space-x-1">
          <Send className="w-3 h-3 text-steel" />
          <p className="text-text-secondary text-sm">{formatDate(shipment.shipDate)}</p>
        </div>
        {shipment.deliveryDate && (
          <div className="flex items-center space-x-1 mt-1">
            <CheckCircle className="w-3 h-3 text-success-green" />
            <p className="text-text-tertiary text-xs">{formatDate(shipment.deliveryDate)}</p>
          </div>
        )}
      </div>

      {/* Status */}
      <div className="col-span-1">
        <div
          className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-mono border ${config.bgColor}`}
        >
          <StatusIcon className={`w-3 h-3 ${config.color}`} />
          <span className={config.color}>{config.label}</span>
        </div>
      </div>

      {/* Products */}
      <div className="col-span-2">
        <div className="flex items-center space-x-1">
          <Package className="w-3 h-3 text-steel" />
          <p className="text-text-secondary text-sm">
            {totalShipped.toLocaleString()} gal
          </p>
        </div>
        <p className="text-text-tertiary text-xs mt-1 truncate">
          {shipment.items.map((i) => i.product.name).join(', ')}
        </p>
      </div>

      {/* Truck / Driver */}
      <div className="col-span-2">
        {shipment.truck ? (
          <div className="flex items-center space-x-1">
            <Truck className="w-3 h-3 text-amber" />
            <p className="text-text-secondary text-sm font-mono">{shipment.truck.unitNumber}</p>
          </div>
        ) : (
          <p className="text-text-tertiary text-xs">No truck assigned</p>
        )}
        {shipment.driver ? (
          <div className="flex items-center space-x-1 mt-1">
            <User className="w-3 h-3 text-steel" />
            <p className="text-text-tertiary text-xs">
              {shipment.driver.firstName} {shipment.driver.lastName}
            </p>
          </div>
        ) : null}
      </div>

      {/* Actions */}
      <div className="col-span-1 flex items-center justify-end space-x-2">
        <Link
          to={`/shipments/${shipment.id}/bol`}
          onClick={(e) => e.stopPropagation()}
          className="p-2 text-text-tertiary hover:text-steel hover:bg-steel/20 transition-all"
          title="View BOL"
        >
          <FileText className="w-4 h-4" />
        </Link>
      </div>
    </Link>
  );
}

export default ShipmentsPage;
